import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GridBlockHeaderOrNull, GridBlockBody} from 'analysis-tool/data-layout/GridBlock';
import EditableTextField from 'ui-library/inputs/EditableTextField';
import Pane, {PaneBorder, HeaderBorder} from 'ui-library/pane/Pane';

import {useDataViewContext} from './DataViewContext';
import DataViewToolbar from './DataViewToolbar';

interface DataViewPaneProps {
  children: React.ReactNode;
}

/**
 * Wrapper for `Pane` that should be used by all subcomponents of `DataView`.
 *
 * TODO(BNCH-121029): Implement functionality for this component.
 *
 * @package
 */
export default function DataViewPane({children}: DataViewPaneProps): JSX.Element {
  const {onTitleChange, title, blockConfig, isChartView, dataTestAttrs} = useDataViewContext();

  const blockContent = (
    <GridBlockBody
      isCollapsible={blockConfig?.isCollapsible ?? undefined}
      isInLayout={isChartView && blockConfig?.isInLayout}
    >
      {children}
    </GridBlockBody>
  );

  if (blockConfig?.blockMode === 'CONTENT_ONLY') {
    return blockContent;
  }

  const titleField = blockConfig ? (
    <GridBlockHeaderOrNull
      // Default to title if not provided
      title={blockConfig.title ?? title}
      subtitle={blockConfig.subtitle ?? undefined}
      infoText={blockConfig.infoText ?? undefined}
    />
  ) : onTitleChange != null ? (
    <EditableTextField
      value={title}
      requestChange={(newTitle: string) => {
        if (newTitle !== title) {
          onTitleChange(newTitle);
        }
      }}
      dataTestAttrs={{component: 'DataViewPane', element: 'editableTextField'}}
    />
  ) : (
    title
  );

  return (
    <Pane
      paneBorder={blockConfig?.isInLayout ? PaneBorder.NO_BORDER : PaneBorder.BOX_SHADOW}
      headerBorder={blockConfig?.isInLayout ? HeaderBorder.NONE : HeaderBorder.SOLID}
      title={<div className={css(styles.badgeContainer)}>{titleField}</div>}
      toolbar={<DataViewToolbar />}
      dataTestAttrs={dataTestAttrs}
    >
      {blockContent}
    </Pane>
  );
}

const styles = StyleSheet.create({
  badgeContainer: {
    alignItems: 'center',
    display: 'flex',
  },
});
